import { useMemo, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LSLocationItem,
  LSLocationState,
  CreateAgreementPayload,
  UseLocalSourcingParams,
  UseLocalSourcingReturn,
} from '../../../types/types';
import { getAgreementOptions } from '../../../api/user';
import { consolidateItemsByRmsNumber } from '../../../utils/consolidateItems';

export function useLocalSourcing({
  customerId,
  searchResult,
  setsearchResult,
  setagreementOptions,
  offerCodeValue,
}: UseLocalSourcingParams): UseLocalSourcingReturn {
  const location = useLocation();
  // Use window.location.search — React Router's location.search is empty in the micro-frontend shell
  const spendLimitId = new URLSearchParams(window.location.search).get(
    'spendLimitId'
  );
  const isLocalSourcingFlow = !!spendLimitId;

  // eslint-disable-next-line no-console
  console.log(
    '[LS Hook] isLocalSourcingFlow:',
    isLocalSourcingFlow,
    '| spendLimitId:',
    spendLimitId,
    '| customerId:',
    customerId,
    '| location.state:',
    location.state
  );

  // Stabilize location.state — only matters on initial mount
  const lsLocationStateRef = useRef(
    location.state as LSLocationState | undefined
  );

  // Keep refs for values that applyLocalSourcingPayload reads at call time
  const offerCodeRef = useRef(offerCodeValue);
  offerCodeRef.current = offerCodeValue;
  const searchResultRef = useRef(searchResult);
  searchResultRef.current = searchResult;

  // Derive item data from location state (pure computation, memoized)
  const lsSearchResult = useMemo(() => {
    const lsState = lsLocationStateRef.current;
    if (!isLocalSourcingFlow || !lsState) return null;
    const items: LSLocationItem[] = lsState.items || [
      lsState as unknown as LSLocationItem,
    ];
    const storeNum =
      lsState.storeNumber || window.sessionStorage.getItem('storeNumber');
    return items.map((item: LSLocationItem) => ({
      rmsItemNumber: item.rmsItemNumber || item.itemNumber,
      weeklyRate: item.weeklyRate,
      weeklyTerm: item.weeklyTerm,
      cashPrice: item.cashPrice,
      storeNumber: storeNum,
      modelNumber: item.modelNumber || lsState.modelNumber || null,
      brand: item.brand || lsState.brand || null,
      costPerItem: item.amount || lsState.amount || null, // pass the costperItem as amount
      quantity: item.quantity || '1',
    }));
  }, [isLocalSourcingFlow]);

  // Set derived search result into context (runs exactly once)
  const lsSearchResultApplied = useRef(false);
  useEffect(() => {
    if (lsSearchResult && !lsSearchResultApplied.current && !searchResult) {
      lsSearchResultApplied.current = true;
      // eslint-disable-next-line no-console
      console.log(
        '[LS Hook] Setting searchResult from location state:',
        lsSearchResult
      );
      setsearchResult(lsSearchResult);
    }
  }, [lsSearchResult, searchResult, setsearchResult]);

  // Fetch agreement options (runs exactly once for LS flow)
  const optionsFetched = useRef(false);
  useEffect(() => {
    if (!isLocalSourcingFlow || optionsFetched.current) return;
    optionsFetched.current = true;
    const storeNumber = window.sessionStorage.getItem('storeNumber');
    // eslint-disable-next-line no-console
    console.log(
      '[LS Hook] Fetching agreementOptions | customerId:',
      customerId,
      '| storeNumber:',
      storeNumber
    );
    const fetchOptions = async () => {
      const response = await getAgreementOptions(customerId, storeNumber);
      // eslint-disable-next-line no-console
      console.log('[LS Hook] ', response?.status, response?.data);
      if (response?.status === 200) {
        setagreementOptions(response.data);
      }
    };
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalSourcingFlow, customerId]);

  // Payload enrichment — stable callback, reads latest values via refs
  const applyLocalSourcingPayload = useCallback(
    (payload: CreateAgreementPayload) => {
      const lsState = lsLocationStateRef.current;
      if (!isLocalSourcingFlow || !lsState) return payload;
      payload.spendLimitId = spendLimitId;
      payload.agreementSource = 'LSRC';
      payload.vendorName = lsState.vendorName;
      payload.vendorId = lsState.vendorId || null;
      // Ensure promotions are preserved for LS flow
      const currentOfferCode = offerCodeRef.current;
      if (!payload.promoCode && currentOfferCode && currentOfferCode !== '') {
        payload.promoCode = currentOfferCode;
      }
      // Enrich items with brand, model, costPerItem, supplierName, supplierOrderNumber, condition
      const currentSearchResult = searchResultRef.current;
      if (payload.items && currentSearchResult) {
        const rawLsItems: LSLocationItem[] = lsState.items || [
          lsState as unknown as LSLocationItem,
        ];
        // Consolidate lsItems by rmsItemNumber so indices align with the
        // already-consolidated payload.items (from consolidateItemsByRmsNumber)
        const lsItemsWithKey = rawLsItems.map((item) => ({
          ...item,
          rmsItemNumber: item.rmsItemNumber || item.itemNumber,
        }));
        const consolidatedLsItems = consolidateItemsByRmsNumber(lsItemsWithKey);
        payload.items = payload.items.map(
          (item: Record<string, unknown>, index: number) => ({
            ...item,
            rmsItemQuantity:
              consolidatedLsItems[index]?.quantity ||
              (item.rmsItemQuantity as string) ||
              '1',
            brand: currentSearchResult[index]?.brand || lsState.brand || null,
            model:
              currentSearchResult[index]?.modelNumber ||
              lsState.modelNumber ||
              null,
            costPerItem:
              currentSearchResult[index]?.costPerItem ||
              consolidatedLsItems[index]?.amount ||
              lsState.amount ||
              null,
            supplierName: lsState.supplierName || lsState.vendorName || null,
            supplierOrderNumber: lsState.supplierOrderNumber || null,
            condition: 'NEW',
          })
        );
      }
      return payload;
    },
    [isLocalSourcingFlow, spendLimitId]
  );

  return {
    isLocalSourcingFlow,
    applyLocalSourcingPayload,
  };
}
